import { useRef, useState } from 'react';
import {
  ChevronRight,
  CircleCheckBig,
  CircleX,
  List,
  MessageSquare,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useGetAgreeableClausesQuery,
  useGetAgreeableSummaryQuery,
  useMarkClauseAsAgreeableMutation,
  useUnmarkClauseAsAgreeableMutation,
} from '@/apis/extended/agreeableClauseApi';
import { useGetContractPrioritiesQuery } from '@/apis/extended/contractApi';
import { useGenerateRatifiedContractMutation } from '@/apis/extended/contractRatificationApi';
import { useSendMessageMutation } from '@/apis/extended/messageApi';
import {
  useCreateNegotiationMutation,
  useGetCounterProposalQuery,
  useUpdateNegotiationMutation,
} from '@/apis/extended/negotiationApi';
import {
  useCreateBatchProposalsMutation,
  useCreateProposalMutation,
  useGetContractProposalsQuery,
} from '@/apis/extended/proposalApi';
import { ClauseProposal } from '@/components/Clause/ClauseProposal';
import { NegotiationClause } from '@/components/Clause/NegotiationClause';
import { useSellerContractClauses } from '@/components/Layout/Seller/Constant/sellerContractClause';
import { ComprehensiveClauseDisplay } from '@/components/Negotiation/ComprehensiveClauseDisplay';
import ComprehensiveNegotiationWorkflow from '@/components/Negotiation/ComprehensiveNegotiationWorkflow';
import { NegotiationStatusBar } from '@/components/Negotiation/NegotiationStatusBar';
import { PartySignersPanel } from '@/components/Negotiation/PartySignersPanel';
import { ProposalClauseDisplay } from '@/components/Negotiation/ProposalClauseDisplay';
import { ProposalSubmissionPanel } from '@/components/Negotiation/ProposalSubmissionPanel';
import { ProposeDictionaryClauseCard } from '@/components/Negotiation/ProposeDictionaryClauseCard';
import SellerWaitingState from '@/components/Negotiation/SellerWaitingState';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useClauseState } from '@/hooks/useClauseState';
import { useContractData } from '@/hooks/useContractData';
import {
  ClauseStatusData,
  getRole,
  ProposalData,
  RemoveClauseStatusData,
  RemoveProposalData,
  RemoveSubsetClauseStatusData,
  SetClauseStatusData,
  SetProposalData,
  SetSubsetClauseStatusData,
  SubsetClauseStatusData,
} from '@/hooks/usestoragedata';
import { CounterProposalState } from '@/types/NegotiationType';
import {
  ClauseStatus,
  ContractClauseStatus,
  ContractStatus,
} from '@/types/StatusType';
import ContractClauseSelectStatus from '../../SellerAgent/Negotiation/Component/ClauseStatus';
import ContractOverallStatus from '../../SellerAgent/Negotiation/Component/ContractOverallStatus';
import NegotiationHeader from '../../SellerAgent/Negotiation/Component/Negotiationheader';
import { ClauseStatusBadge } from '../../SellerAgent/Negotiation/Component/statusbadge';

type SellerNegotiateContractPageProps = {
  contractIdOverride?: string;
  embedded?: boolean;
};

const SellerNegotiateContractPage = ({
  contractIdOverride,
  embedded = false,
}: SellerNegotiateContractPageProps = {}) => {
  const { contractId: contractIdParam } = useParams();
  const navigate = useNavigate();

  const contractId = contractIdOverride ?? contractIdParam ?? '';

  // Use unified contract data hook with userRole to merge proposals
  const {
    contract,
    isLoading: contractLoading,
    error: contractError,
    isRatified: contractRatified,
    canNegotiate,
    comprehensiveClauses,
    refetch: refetchContract,
  } = useContractData(contractId, 'seller');

  const {
    clauses,
    attentionClauses,
    urgentClauses,
    negotiationSummary,
    hasPendingNegotiations,
    isLoadingClauses,
    isErrorClauses,
    handleCounterClause,
    handleAcceptClause,
    handleRejectClause,
    canActOnClause,
    getClausesRequiringUserAttention,
    getUrgentClausesForUser,
  } = useClauseState({
    contractId,
    userRole: 'seller',
    contractRatified,
  });

  // Legacy state for backward compatibility
  const contractClauses = useSellerContractClauses(contractId);
  const [selectedSection, setSelectedSection] = useState(0);
  const [showTableOfContents, setShowTableOfContents] = useState(true);
  const [counterProposal, setCounterProposal] = useState<CounterProposalState>(
    {}
  );
  const [clauseStatuses, setClauseStatuses] = useState<
    Record<string, ClauseStatus>
  >({});
  const [subsetClauseStatuses, setSubsetClauseStatuses] = useState<
    Record<string, ClauseStatus>
  >({});
  const [contractStatus, setContractStatus] = useState<
    | 'draft'
    | 'under_review'
    | 'suggested'
    | 'counter_proposed'
    | 'contingent'
    | 'rejected'
    | 'take_it_or_leave_it'
    | 'expired'
    | 'agreeable'
    | 'voided'
    | 'finalized'
    | 'reopened'
  >('draft');
  const [proposals, setProposals] = useState<Record<string, ProposalData>>({});

  // Batch proposal state
  const [batchProposals, setBatchProposals] = useState<
    Map<
      string,
      {
        clauseId: string;
        clauseHierarchicalId: string;
        clauseTitle: string;
        originalValue: any;
        proposedValue: any;
        message?: string;
      }
    >
  >(new Map());

  const [updateNegotiation, { isLoading: isUpdating }] =
    useUpdateNegotiationMutation();
  const [createNegotiation, { isLoading: isCreating }] =
    useCreateNegotiationMutation();
  const [createProposal] = useCreateProposalMutation();

  // Get all proposals for this contract (for ProposalClauseDisplay)
  const { data: allProposalsData } = useGetContractProposalsQuery(
    { contractId },
    { skip: !contractId }
  );

  // Handle both array and object responses from API
  const allProposals = Array.isArray(allProposalsData)
    ? allProposalsData
    : allProposalsData?.proposals || [];

  // Get agreeable clauses from backend
  const { data: agreeableClausesData = [] } = useGetAgreeableClausesQuery(
    contractId,
    {
      skip: !contractId,
    }
  );

  // Get priority clauses set by agent (NEW SIMPLE VERSION)
  const { data: priorityData } = useGetContractPrioritiesQuery(contractId, {
    skip: !contractId,
  });

  const priorityClauseIds = priorityData?.priorities || [];

  // API mutations
  const [markAsAgreeable] = useMarkClauseAsAgreeableMutation();
  const [unmarkAsAgreeable] = useUnmarkClauseAsAgreeableMutation();
  const [generateRatifiedContract, { isLoading: isGeneratingContract }] =
    useGenerateRatifiedContractMutation();
  const [sendMessage] = useSendMessageMutation();

  // Convert to Sets for easy lookup
  const agreeableClauses = new Set(
    agreeableClausesData.map((c: any) => c.clauseId)
  );
  const priorityClauses = new Set(priorityClauseIds);

  // Legacy counter-proposal query - disabled in favor of new proposal system
  // const { data: counterProposalData } = useGetCounterProposalQuery(contractId, {
  //   skip: !contractId,
  //   refetchOnMountOrArgChange: true,
  // });

  // Enhanced clause handling methods
  const handleClauseCounter = async (clauseId: string, reason?: string) => {
    try {
      await handleCounterClause(clauseId, reason);
      toast.success('Clause countered successfully');
    } catch (error) {
      console.error('Error countering clause:', error);
      toast.error('Failed to counter clause');
    }
  };

  const handleClauseAccept = async (clauseId: string, reason?: string) => {
    try {
      await handleAcceptClause(clauseId, reason);
      toast.success('Clause accepted successfully');
    } catch (error) {
      console.error('Error accepting clause:', error);
      toast.error('Failed to accept clause');
    }
  };

  const handleClauseReject = async (clauseId: string, reason?: string) => {
    try {
      await handleRejectClause(clauseId, reason);
      toast.success('Clause rejected successfully');
    } catch (error) {
      console.error('Error rejecting clause:', error);
      toast.error('Failed to reject clause');
    }
  };

  const handleClauseEdit = (_clauseId: string) => {};
  const handleAddendum = (_clauseId: string) => {};
  const handleExtension = (_clauseId: string) => {};
  const handleRepairRequest = (_clauseId: string) => {};

  const handleProposalCreated = async (
    clauseId: string,
    clauseHierarchicalId: string,
    originalValue: any,
    proposedValue: any,
    message?: string,
    linkedClauses?: any[]
  ) => {
    if (!contractId) {
      toast.error('Contract ID is missing - cannot create proposal');
      return;
    }
    try {
      await createProposal({
        contractId,
        clauseId,
        clauseHierarchicalId,
        originalValue,
        proposedValue,
        message,
        linkedClauses: linkedClauses || [],
      }).unwrap();
      const linkedText =
        linkedClauses && linkedClauses.length > 0
          ? ` with ${linkedClauses.length} linked clause${
              linkedClauses.length > 1 ? 's' : ''
            }`
          : '';
      toast.success(`Proposal created successfully${linkedText}`);
    } catch (error) {
      toast.error('Failed to create proposal');
    }
  };

  const handleValueAccepted = async (clauseId: string, value: any) => {
    // Find the clause to get hierarchical ID
    const clause = comprehensiveClauses
      .flatMap((cat) => cat.clauses)
      .find((c) => c.id === clauseId);

    if (!clause) {
      toast.error('Clause not found');
      return;
    }

    try {
      await markAsAgreeable({
        contractId,
        clauseId,
        clauseHierarchicalId: clause.hierarchicalId || clauseId,
        agreedValue: value,
      }).unwrap();

      toast.success('Clause marked as agreeable and saved');
    } catch (error) {
      console.error('Error marking clause as agreeable:', error);
      toast.error('Failed to mark clause as agreeable');
    }
  };

  const handleValueRejected = async (clauseId: string) => {
    try {
      await unmarkAsAgreeable({ contractId, clauseId }).unwrap();
      toast.error('Clause removed from agreeable list');
    } catch (error) {
      console.error('Error unmarking clause:', error);
      toast.error('Failed to unmark clause');
    }
  };

  // Batch proposal handlers
  const handleAddToBatch = (
    clauseId: string,
    clauseHierarchicalId: string,
    clauseTitle: string,
    originalValue: any,
    proposedValue: any,
    message?: string
  ) => {
    const newBatch = new Map(batchProposals);
    newBatch.set(clauseId, {
      clauseId,
      clauseHierarchicalId,
      clauseTitle,
      originalValue,
      proposedValue,
      message,
    });
    setBatchProposals(newBatch);
  };

  const handleRemoveFromBatch = (clauseId: string) => {
    const newBatch = new Map(batchProposals);
    newBatch.delete(clauseId);
    setBatchProposals(newBatch);
  };

  const handleClearBatch = () => {
    setBatchProposals(new Map());
  };

  const handleBatchSubmitSuccess = () => {
    // Clear batch after successful submission
    setBatchProposals(new Map());
  };

  const handleTOCClick = (index: number) => {
    setSelectedSection(index);
    if (index < contractClauses.length) {
      cardRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleClauseStatusChange = (
    clauseIndex: number,
    status: ClauseStatus
  ) => {
    const clause = contractClauses[clauseIndex];
    if (!clause) return;

    setClauseStatuses((prev) => ({
      ...prev,
      [clauseIndex]: status,
    }));

    // Update storage
    SetClauseStatusData(clauseIndex, status);
  };

  const handleSubsetClauseStatusChange = (
    clauseIndex: number,
    subsetIndex: number,
    status: ClauseStatus
  ) => {
    const key = `${clauseIndex}-${subsetIndex}`;
    setSubsetClauseStatuses((prev) => ({
      ...prev,
      [key]: status,
    }));

    // Update storage
    SetSubsetClauseStatusData(key, status);
  };

  const handleProposalChange = (
    clauseIndex: number,
    proposal: ProposalData
  ) => {
    setProposals((prev) => ({
      ...prev,
      [clauseIndex]: proposal,
    }));

    // Update storage
    SetProposalData(clauseIndex, proposal);
  };

  const handleCounterProposalChange = (field: string, value: any) => {
    setCounterProposal((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitCounterProposal = async () => {
    try {
      const proposalData = {
        contractId: contractId,
        offeredPrice: counterProposal.offeredPrice || {
          value: '',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
        EarnestMoney: counterProposal.EarnestMoney || {
          value: '',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
        ClosingDate: counterProposal.ClosingDate || {
          value: '',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
        AppraisalContingency: counterProposal.AppraisalContingency || {
          value: '',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
        HomeInspection: counterProposal.HomeInspection || {
          value: '',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
        FinancingContingency: counterProposal.FinancingContingency || {
          value: '',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
        postCloseOccupancy: counterProposal.postCloseOccupancy || {
          value: '',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
        proposedBy: {
          value: 'seller',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
        NegotiationStatus: {
          value: 'counter_offer',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
        Suggested: {
          value: '',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
        Contingent: {
          value: '',
          status: 'agreeable',
          subsetClauseStatus: 'agreeable',
        },
      };

      await createCounterProposal(proposalData).unwrap();
      toast.success('Counter-proposal submitted successfully');

      // Reset form
      setCounterProposal({});
    } catch (error) {
      console.error('Error submitting counter-proposal:', error);
      toast.error('Failed to submit counter-proposal');
    }
  };

  const handleUpdateCounterProposal = async () => {
    try {
      const proposalData = {
        contractId: contractId,
        ...counterProposal,
      };

      await updateCounterProposal(proposalData).unwrap();
      toast.success('Counter-proposal updated successfully');
    } catch (error) {
      console.error('Error updating counter-proposal:', error);
      toast.error('Failed to update counter-proposal');
    }
  };

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Early return if no contractId
  if (!contractId) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="text-center py-8">
            <CircleX className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Invalid Contract ID
            </h3>
            <p className="text-gray-500">
              No contract ID provided. Please check the URL and try again.
            </p>
            <Button onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoadingClauses) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Loading Contract Clauses
            </h3>
            <p className="text-gray-500">
              Please wait while we load the contract details...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (isErrorClauses) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="text-center py-8">
            <CircleX className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error Loading Contract
            </h3>
            <p className="text-gray-500">
              There was an error loading the contract clauses. Please try again.
            </p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Awaiting Signatures View
  if (
    contract?.contractStatus === 'awaiting_signatures' ||
    contract?.contractStatus === 'awaiting_seller_signature'
  ) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <Card className="border-blue-500 bg-gradient-to-br from-blue-50 to-white">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-blue-800 flex items-center gap-3">
              <svg
                className="h-10 w-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Contract Ready for Signatures
            </CardTitle>
            <p className="text-blue-700 mt-2">
              Negotiation complete! All agreed values have been applied. Now
              both parties need to sign.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Contract Summary */}
            <div className="bg-white p-6 rounded-lg border shadow-sm space-y-3">
              <h3 className="font-semibold text-lg text-gray-900 mb-4">
                Contract Summary
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Property Address</p>
                  <p className="font-medium">
                    {contract?.product?.address?.fullAddress || 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Purchase Price</p>
                  <p className="font-medium text-green-700">
                    $
                    {contract?.offerSpecifics?.offerPrice?.toLocaleString() ||
                      'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Closing Date</p>
                  <p className="font-medium">
                    {contract?.timeline?.closingDate
                      ? new Date(
                          contract.timeline.closingDate
                        ).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <p className="font-medium text-blue-600">
                    {contract?.contractStatus.replace(/_/g, ' ').toUpperCase()}
                  </p>
                </div>
              </div>
            </div>

            {/* Signature Status */}
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-lg text-blue-900 mb-4">
                Signature Status
              </h3>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <svg
                    className={`h-6 w-6 ${
                      contract?.buyerSign?.isStored
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="font-medium">Buyer Signature</p>
                    <p className="text-sm text-gray-600">
                      {contract?.buyerSign?.isStored
                        ? `Signed by ${contract.buyerSign.name || 'Buyer'} on ${
                            contract.buyerSign.signedAt
                              ? new Date(
                                  contract.buyerSign.signedAt
                                ).toLocaleDateString()
                              : 'N/A'
                          }`
                        : 'Waiting for buyer to sign...'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <svg
                    className={`h-6 w-6 ${
                      contract?.sellerSign?.isStored
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="font-medium">Seller Signature (You)</p>
                    <p className="text-sm text-gray-600">
                      {contract?.sellerSign?.isStored
                        ? `Signed by ${
                            contract.sellerSign.name || 'Seller'
                          } on ${
                            contract.sellerSign.signedAt
                              ? new Date(
                                  contract.sellerSign.signedAt
                                ).toLocaleDateString()
                              : 'N/A'
                          }`
                        : contract?.contractStatus ===
                          'awaiting_seller_signature'
                        ? '✅ Ready for your signature!'
                        : 'Waiting for buyer to sign first...'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps / Actions */}
            <div className="bg-gray-50 p-6 rounded-lg border">
              <h3 className="font-semibold text-lg text-gray-900 mb-4">
                Next Steps
              </h3>
              {contract?.contractStatus === 'awaiting_signatures' ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">1.</span> The buyer needs to
                    review and sign the contract first
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">2.</span> Once the buyer
                    signs, you'll be notified
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">3.</span> You can then
                    provide your signature to complete ratification
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-green-700 font-semibold">
                    ✅ The buyer has signed! It's your turn to sign now.
                  </p>
                  <p className="text-sm text-gray-700">
                    Review the contract terms and provide your signature to
                    complete the ratification process.
                  </p>
                </div>
              )}
            </div>

            {/* Signature Section - Show when contract is ready */}
            {(contract?.contractStatus === 'awaiting_signatures' ||
              contract?.contractStatus === 'awaiting_seller_signature') &&
              !contract?.sellerSign?.isStored && (
                <Card className="bg-yellow-50 border-yellow-400">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <svg
                        className="h-8 w-8 text-yellow-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div>
                        <h3 className="text-xl font-bold text-yellow-900">
                          Contract Ready for Your Signature
                        </h3>
                        <p className="text-sm text-yellow-800">
                          All negotiations are complete. Please review and sign
                          the contract.
                          {contract?.buyerSign?.isStored && (
                            <span className="font-semibold">
                              {' '}
                              The buyer has already signed.
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="lg"
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      onClick={() =>
                        navigate(`/seller/contract/${contractId}/sign`)
                      }
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                      Sign Contract Now
                    </Button>
                  </CardContent>
                </Card>
              )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/view-contract/${contractId}`)}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                View Full Contract
              </button>
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Back to Dashboard
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fully Ratified Contract View
  if (
    contractRatified ||
    contract?.isRatified ||
    contract?.contractStatus === 'ratified'
  ) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <Card className="border-green-500 bg-gradient-to-br from-green-50 to-white">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-green-800 flex items-center gap-3">
              <svg
                className="h-10 w-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Contract Fully Ratified!
            </CardTitle>
            <p className="text-green-700 mt-2">
              Congratulations! This purchase agreement has been executed and is
              now legally binding.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Contract Summary */}
            <div className="bg-white p-6 rounded-lg border shadow-sm space-y-3">
              <h3 className="font-semibold text-lg text-gray-900 mb-4">
                Contract Summary
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Property Address</p>
                  <p className="font-medium">
                    {contract?.product?.address?.fullAddress || 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Purchase Price</p>
                  <p className="font-medium text-green-700">
                    $
                    {contract?.offerSpecifics?.offerPrice?.toLocaleString() ||
                      'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Closing Date</p>
                  <p className="font-medium">
                    {contract?.timeline?.closingDate
                      ? new Date(
                          contract.timeline.closingDate
                        ).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Ratified On</p>
                  <p className="font-medium">
                    {contract?.ratifiedAt
                      ? new Date(contract.ratifiedAt).toLocaleDateString(
                          'en-US',
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }
                        )
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Signature Status */}
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-lg text-blue-900 mb-4">
                Signature Status
              </h3>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <svg
                    className={`h-6 w-6 ${
                      contract?.buyerSign?.isStored
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="font-medium">Buyer Signature</p>
                    <p className="text-sm text-gray-600">
                      {contract?.buyerSign?.isStored
                        ? `Signed by ${contract.buyerSign.name || 'Buyer'} on ${
                            contract.buyerSign.signedAt
                              ? new Date(
                                  contract.buyerSign.signedAt
                                ).toLocaleDateString()
                              : 'N/A'
                          }`
                        : 'Not signed'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <svg
                    className={`h-6 w-6 ${
                      contract?.sellerSign?.isStored
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="font-medium">Seller Signature (You)</p>
                    <p className="text-sm text-gray-600">
                      {contract?.sellerSign?.isStored
                        ? `Signed by ${
                            contract.sellerSign.name || 'Seller'
                          } on ${
                            contract.sellerSign.signedAt
                              ? new Date(
                                  contract.sellerSign.signedAt
                                ).toLocaleDateString()
                              : 'N/A'
                          }`
                        : 'Not signed'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps / Actions */}
            <div className="bg-gray-50 p-6 rounded-lg border">
              <h3 className="font-semibold text-lg text-gray-900 mb-4">
                Next Steps
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">1.</span>
                  <span>
                    Monitor contingency deadlines in your contract dashboard
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">2.</span>
                  <span>
                    Coordinate with buyer on inspections and appraisals
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">3.</span>
                  <span>
                    Prepare for closing on{' '}
                    {contract?.timeline?.closingDate
                      ? new Date(
                          contract.timeline.closingDate
                        ).toLocaleDateString()
                      : 'scheduled date'}
                  </span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/view-contract/${contractId}`)}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                View Full Contract
              </button>
              <button
                onClick={() =>
                  navigate(`/seller/property-dashboard/${contractId}`)
                }
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Sale Dashboard
              </button>
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Back to Properties
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if seller is waiting for buyer response after submitting BATCH proposals
  // Show waiting state ONLY if:
  // 1. Seller has submitted proposals to buyer
  // 2. All submitted proposals are still pending (not yet accepted/rejected by buyer)
  // 3. Almost ALL clauses are agreeable (batch submission scenario)
  // 4. Contract is not ratified
  //
  // Logic: Waiting state = seller marked everything agreeable EXCEPT the batch proposals
  // This means: agreeableClauses + sellerProposals ≈ totalClauses
  const sellerProposalsToBuyer =
    allProposals?.filter(
      (p: any) => p.proposedBy === 'seller' && p.status === 'pending'
    ) || [];

  const buyerProposalsToSeller =
    allProposals?.filter((p: any) => p.proposedBy === 'buyer') || [];

  const totalClauses = comprehensiveClauses.flatMap(
    (cat) => cat.clauses
  ).length;
  const agreeableCount = agreeableClauses.size;
  const pendingSellerProposals = sellerProposalsToBuyer.length;

  // Seller is waiting ONLY if they submitted batch proposals (meaning they agreed to everything else)
  // Heuristic: If agreeable + proposals ≥ 80% of total clauses, it's a batch scenario
  const isBatchProposalScenario =
    totalClauses > 0 &&
    (agreeableCount + pendingSellerProposals) / totalClauses >= 0.8;

  const isSellerWaitingForBuyer =
    contract &&
    sellerProposalsToBuyer.length > 0 &&
    buyerProposalsToSeller.length === 0 && // Seller has countered, now waiting for buyer
    isBatchProposalScenario && // Only show waiting state for batch proposals
    !contractRatified &&
    (contract.contractStatus === 'draft' ||
      (contract as any).contractStatus === 'negotiating');

  // Handler for sending message to buyer
  const handleSendMessageToBuyer = async (messageText: string) => {
    await sendMessage({
      contractId,
      toRole: 'buyer',
      message: messageText,
    }).unwrap();
  };

  // Seller Waiting State - Show when seller submitted proposals and waiting for buyer
  if (isSellerWaitingForBuyer) {
    return (
      <SellerWaitingState
        contract={contract}
        contractId={contractId}
        pendingProposalsCount={sellerProposalsToBuyer.length}
        onSendMessage={handleSendMessageToBuyer}
      />
    );
  }

  // No clauses available
  if (comprehensiveClauses.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="text-center py-8">
            <CircleX className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Contract Clauses Available
            </h3>
            <p className="text-gray-500">
              This contract doesn't have any comprehensive clauses available for
              negotiation. The contract data may be incomplete or the clause
              extraction failed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className={
        embedded
          ? 'w-full'
          : 'taste-negotiate-page container mx-auto px-4 py-8 taste-rise'
      }
    >
      <NegotiationHeader
        contractId={contractId}
        onBack={embedded ? undefined : () => navigate(-1)}
        canNegotiate={canNegotiate}
      />

      {canNegotiate && contractId && (
        <div className="space-y-4 mt-4">
          <ProposeDictionaryClauseCard contractId={contractId} />
          {contract && <PartySignersPanel contract={contract} />}
        </div>
      )}

      {/* Unified Negotiation View with Table of Contents */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
        {/* Table of Contents - Left Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <List className="h-5 w-5" />
                  Table of Contents
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-1">
                  {/* Priority Section */}
                  {priorityClauses.size > 0 && (
                    <div className="mb-4">
                      <div className="text-xs font-semibold text-yellow-600 mb-2 px-2">
                        ⭐ AGENT PRIORITIES
                      </div>
                      {comprehensiveClauses.map((category) =>
                        category.clauses
                          .filter(
                            (clause) =>
                              priorityClauses.has(clause.id) &&
                              !agreeableClauses.has(clause.id)
                          )
                          .map((clause) => (
                            <button
                              key={clause.id}
                              onClick={() => {
                                const element = document.getElementById(
                                  `clause-${clause.id}`
                                );
                                element?.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'center',
                                });
                              }}
                              className="w-full text-left p-2 rounded-md text-sm transition-colors bg-yellow-100 border border-yellow-400 hover:bg-yellow-200"
                            >
                              <div className="flex items-center justify-between">
                                <span className="truncate font-medium text-yellow-800">
                                  {clause.title}
                                </span>
                                <ChevronRight className="h-4 w-4 flex-shrink-0 text-yellow-600" />
                              </div>
                            </button>
                          ))
                      )}
                    </div>
                  )}

                  {/* All Clauses by Category */}
                  {comprehensiveClauses.map((category, catIndex) => (
                    <div key={catIndex} className="mb-3">
                      <div className="font-semibold text-sm text-gray-700 mb-2 px-2">
                        {category.title}
                      </div>
                      {category.clauses
                        .filter(
                          (clause) =>
                            !agreeableClauses.has(clause.id) &&
                            !priorityClauses.has(clause.id)
                        )
                        .map((clause) => (
                          <button
                            key={clause.id}
                            onClick={() => {
                              const element = document.getElementById(
                                `clause-${clause.id}`
                              );
                              element?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                              });
                            }}
                            className="w-full text-left p-2 rounded-md text-sm transition-colors hover:bg-blue-50 hover:text-blue-700"
                          >
                            <div className="flex items-center justify-between">
                              <span className="truncate">{clause.title}</span>
                              <ChevronRight className="h-4 w-4 flex-shrink-0" />
                            </div>
                          </button>
                        ))}
                    </div>
                  ))}
                  {agreeableClauses.size ===
                    comprehensiveClauses.flatMap((cat) => cat.clauses)
                      .length && (
                    <div className="text-center py-4 text-green-600 text-sm font-medium">
                      ✓ All clauses agreed
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Content - Right Side */}
        <div className="lg:col-span-3">
          <div className="space-y-6">
            {/* Negotiation Status Bar with Progress */}
            <NegotiationStatusBar
              totalClauses={
                comprehensiveClauses.flatMap((cat) => cat.clauses).length
              }
              agreedClauses={agreeableClauses.size}
              pendingProposals={buyerProposalsToSeller.length}
              priceImpact={buyerProposalsToSeller.reduce(
                (total: number, p: any) => {
                  if (
                    p.clauseId === 'purchase-price' ||
                    p.clauseId === 'offer-price'
                  ) {
                    return (
                      total +
                      (Number(p.proposedValue) - Number(p.originalValue))
                    );
                  }
                  if (
                    p.clauseId === 'seller-assistance-amount' ||
                    p.clauseId === 'seller-assistance'
                  ) {
                    return (
                      total +
                      (Number(p.proposedValue) - Number(p.originalValue))
                    );
                  }
                  return total;
                },
                0
              )}
              userRole="seller"
            />

            {/* Seller View: ALL clauses from contract (unless marked agreeable) */}
            {comprehensiveClauses.length > 0 ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Contract Review
                  </h3>
                  <div className="flex items-center gap-4">
                    {/* Show "View All Proposals" button if there are buyer proposals requiring seller's response */}
                    {buyerProposalsToSeller.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Scroll to top to see all proposals
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                          toast.info(
                            `${buyerProposalsToSeller.length} buyer proposal${
                              buyerProposalsToSeller.length > 1 ? 's' : ''
                            } require your response`
                          );
                        }}
                        className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        View Buyer Proposals ({buyerProposalsToSeller.length})
                      </Button>
                    )}
                    <div className="text-sm text-gray-600">
                      {agreeableClauses.size} of{' '}
                      {
                        comprehensiveClauses.flatMap((cat) => cat.clauses)
                          .length
                      }{' '}
                      clauses marked agreeable
                    </div>
                  </div>
                </div>

                {/* Priority Clauses (Set by Agent) */}
                {priorityClauses.size > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-yellow-500"></div>
                      <h4 className="text-lg font-semibold text-yellow-600">
                        ⭐ Agent Priorities - Review These First
                      </h4>
                    </div>
                    {comprehensiveClauses.map((category) =>
                      category.clauses
                        .filter(
                          (clause) =>
                            priorityClauses.has(clause.id) &&
                            !agreeableClauses.has(clause.id)
                        )
                        .map((clause) => (
                          <div
                            key={`priority-${clause.id}`}
                            id={`clause-${clause.id}`}
                            className="scroll-mt-20"
                          >
                            <div className="relative">
                              <div className="absolute -left-2 top-0 bottom-0 w-1 bg-yellow-500 rounded"></div>
                              <ClauseProposal
                                clauseId={clause.id}
                                clauseHierarchicalId={
                                  clause.hierarchicalId || clause.id
                                }
                                title={clause.title}
                                description={clause.description}
                                originalValue={clause.originalValue}
                                currentValue={clause.value}
                                isNegotiable={clause.isNegotiable}
                                isRequired={clause.isRequired}
                                validation={clause.validation}
                                userRole="seller"
                                onProposalCreated={handleProposalCreated}
                                onValueAccepted={handleValueAccepted}
                                onValueRejected={handleValueRejected}
                                onAddToBatch={handleAddToBatch}
                                isPriority={true}
                                isSelected={batchProposals.has(clause.id)}
                                className="w-full border-yellow-400/50 bg-yellow-50"
                              />
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                )}

                {/* Regular Clauses */}
                {comprehensiveClauses.map((category, catIndex) => (
                  <div key={catIndex} className="space-y-4">
                    <h4 className="text-xl font-semibold text-gray-800 mt-6">
                      {category.title}
                    </h4>
                    {category.clauses
                      .filter(
                        (clause) =>
                          !agreeableClauses.has(clause.id) &&
                          !priorityClauses.has(clause.id)
                      )
                      .map((clause) => (
                        <div
                          key={clause.id}
                          id={`clause-${clause.id}`}
                          className="scroll-mt-20"
                        >
                          <ClauseProposal
                            clauseId={clause.id}
                            clauseHierarchicalId={
                              clause.hierarchicalId || clause.id
                            }
                            title={clause.title}
                            description={clause.description}
                            originalValue={clause.originalValue}
                            currentValue={clause.value}
                            isNegotiable={clause.isNegotiable}
                            isRequired={clause.isRequired}
                            validation={clause.validation}
                            userRole="seller"
                            onProposalCreated={handleProposalCreated}
                            onValueAccepted={handleValueAccepted}
                            onValueRejected={handleValueRejected}
                            onAddToBatch={handleAddToBatch}
                            isSelected={batchProposals.has(clause.id)}
                            className="w-full"
                          />
                        </div>
                      ))}
                  </div>
                ))}

                {/* Ratification Section — shown when all clauses agreed OR agent confirmed complete */}
                {(agreeableClauses.size ===
                  comprehensiveClauses.flatMap((cat) => cat.clauses).length ||
                  (contract?.contractStatus as string) ===
                    'negotiations_complete') && (
                  <Card className="border-green-500 bg-green-50">
                    <CardHeader>
                      <CardTitle className="text-green-800 flex items-center gap-2">
                        <CircleCheckBig className="h-6 w-6" />
                        {(contract?.contractStatus as string) ===
                        'negotiations_complete'
                          ? 'Negotiations Complete - Ready for Ratification'
                          : 'All Clauses Agreed - Ready for Ratification'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-gray-700">
                        {(contract?.contractStatus as string) ===
                        'negotiations_complete'
                          ? 'The agent has confirmed all negotiations are complete. Generate the final contract to proceed to signatures.'
                          : 'All contract clauses have been reviewed and marked as agreeable. You can now generate and sign the final contract.'}
                      </p>
                      <Button
                        size="lg"
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        disabled={isGeneratingContract}
                        onClick={async () => {
                          try {
                            toast.info(
                              'Applying all agreed values to contract...'
                            );
                            const result = await generateRatifiedContract({
                              contractId,
                            }).unwrap();

                            toast.success(
                              result.message ||
                                'Contract ready for signatures! Buyer must sign first.'
                            );

                            // Refetch contract to get updated status
                            await refetchContract();

                            // Navigate to signature page (same contract ID)
                            navigate(`/seller/contract/${contractId}/sign`);
                          } catch (error: any) {
                            console.error(
                              'Error applying negotiated values:',
                              error
                            );
                            toast.error(
                              error.data?.message ||
                                error.data?.error ||
                                'Failed to apply negotiated values. Please try again.'
                            );
                          }
                        }}
                      >
                        {isGeneratingContract ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5 mr-2"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Generating Contract...
                          </>
                        ) : (
                          <>
                            <CircleCheckBig className="h-5 w-5 mr-2" />
                            Generate Ratified Contract
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="text-center py-8">
                  <p className="text-gray-500">
                    No clauses available for negotiation yet.
                  </p>
                </CardContent>
              </Card>
            )}

            {!canNegotiate && (
              <Card>
                <CardContent className="text-center py-8">
                  <CircleX className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Negotiation Not Available
                  </h3>
                  <p className="text-gray-500">
                    This contract is not available for negotiation at this time.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Proposal Submission Panel - Sticky Footer */}
      <ProposalSubmissionPanel
        contractId={contractId}
        selectedProposals={batchProposals}
        onClearAll={handleClearBatch}
        onRemoveProposal={handleRemoveFromBatch}
        onSubmitSuccess={handleBatchSubmitSuccess}
      />
    </div>
  );
};

export default SellerNegotiateContractPage;
